import {
  Box,
  CssReset,
  NavBar,
  PageHeader,
  SolvTheme,
  Spinner,
  Stack,
  Table,
  TableSection,
  Text,
} from '@solvhealth/jigsaw-v1';
import React, { ComponentProps, ReactNode } from 'react';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import { useSolvSelector } from '~/reducers';
import { useTranslationNavBar } from '~/components/ProviderGroup/hooks/useTranslationNavBar';
import { PGSectionNumber } from './providerGroups/constants';
import { SectionNumber } from './constants';
import { isProviderGroupLocation } from '~/core/util/location';
import { useGeneralConfig } from '~/core/dapi/generalConfig';
import { RemixRoutes } from '~/core/remix';
import { usePreloadedData } from '~/components/Paperwork/hooks';

type Props = Partial<ComponentProps<typeof PageHeader>> & {
  /** Contents of the page */
  children: ReactNode;

  /** Whether to render the page as a table-style layout */
  table?: boolean;

  /** Content to render outside the table (only shown when `table=true`) */
  postTableContent?: ReactNode;

  /** What to do when back is clicked */
  onGoBack?: () => void;

  /** Disable padding around the content when a full-bleed design is needed */
  noPadding?: boolean;

  section: PGSectionNumber | SectionNumber;
};

/**
 * Renders the layout for a paperwork page. Handles both form layouts and tabular layouts.
 */
const PaperworkLayout = ({
  children,
  title,
  onGoBack,
  noPadding,
  subtitle,
  table,
  postTableContent,
  section,
}: Props) => {
  const paperwork = useSolvSelector((state) => state.paperwork);
  const isProviderGroup = useSolvSelector((state) =>
    isProviderGroupLocation(state.location as LocationInterface)
  );
  const { navTitle, navSubtitle } = useTranslationNavBar(
    section,
    isProviderGroup ? PGSectionNumber : SectionNumber,
    paperwork
  );

  if (table) {
    return (
      <Stack>
        <NavBar
          onGoBack={onGoBack}
          progress={paperwork.percentCompleted / 100}
          subtitle={navSubtitle}
        >
          {navTitle}
        </NavBar>
        <Box maxWidth={SolvTheme.breakpoints.sm} mx="auto" width="100%">
          <Table>
            {title && (
              <TableSection>
                {/* Make sure we have pt=4 on the page header here */}
                <Box pt={1}>
                  <PageHeader subtitle={subtitle} title={title} />
                </Box>
              </TableSection>
            )}
            {children}
          </Table>
          {postTableContent}
        </Box>
        <CssReset />
      </Stack>
    );
  }

  return (
    <Stack>
      <NavBar
        onGoBack={onGoBack}
        progress={paperwork.percentCompleted / 100}
        subtitle={navSubtitle}
      >
        {navTitle}
      </NavBar>

      <Stack maxWidth={SolvTheme.breakpoints.sm} mx="auto" pb={8} pt={4} space={3} width="100%">
        {title && (
          <Box px={2}>
            <PageHeader subtitle={subtitle} title={title} />
          </Box>
        )}

        <Stack px={noPadding ? 0 : 2}>{children}</Stack>
      </Stack>
      <CssReset />
    </Stack>
  );
};

function PaperworkCheckRedirect(props: Props) {
  const location = useSolvSelector((state) => state.location);
  const { booking, userProfile } = usePreloadedData();

  const { isLoading, data: usePaperworkV2 } = useGeneralConfig(
    'use_paperwork_v2',
    location.id ?? '',
    true,
    false
  );

  if (isLoading) {
    return (
      <Stack alignItems="center" textAlign="center">
        <Spinner />
        <Text muted>Loading...</Text>
      </Stack>
    );
  }

  /*
    Redirect to paperwork v2 if its enabled, but just only redirect english bookings since we dont have localization yet
   */
  if (usePaperworkV2 && booking?.id && booking?.language === 'en') {
    window.location.replace(RemixRoutes.paperwork(booking?.id, window.location.search));
    return null;
  }

  return <PaperworkLayout {...props} />;
}

export default PaperworkCheckRedirect;
